import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowClockwiseIcon } from '@phosphor-icons/react/dist/csr/ArrowClockwise';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import { CaseFormDialog } from './CaseFormDialog';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { Button } from '../../design-system/primitives/Button/Button';
import {
  DataTable,
  type Column,
  type ListStatus,
  type Row,
} from '../../design-system/composites/DataTable/DataTable';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { Select, type SelectOption } from '../../design-system/primitives/Select/Select';
import { ExportButton } from '../shared/ExportButton';
import { ApiError } from '../../lib/api/client';
import { casesApi, type CaseQuery, type CaseRow, type CaseStatus } from '../../lib/api/cases';
import { formatTimestamp } from '../../lib/format/dateTime';
import { statusLabelKey } from './caseStatusLabel';
import styles from './CaseList.module.css';
import { optionalReferenceName } from '../../lib/format/referenceName';

/**
 * The case register list — filters, cursor-paginated table, "New case".
 * Follows `CodeList.tsx`/`AuditLogScreen.tsx` for the paginator shape (draft
 * vs. applied filters, an opaque cursor read out of `meta.next_cursor` and
 * handed back verbatim, never parsed).
 *
 * ## The status filter renders the WHOLE filtered set, never a client-side
 * narrowing
 *
 * `status` travels to the server exactly as `case_number`/`mobile` do —
 * `casesApi.list` does the filtering; this component never re-derives a
 * subset of a broader response to hide a row the server already returned.
 * A mutant that dropped `status` from the outgoing query, or that filtered
 * client-side on a wider fetch, would still satisfy a test that only checks
 * a wanted row is present — this screen's own test asserts on the ENTIRE
 * rendered row set for exactly that reason (`CaseList.test.tsx`).
 *
 * ## `New case` is gated on `case.create`, never on reaching this screen at
 * all
 *
 * Reaching `/cases` already proves `case.view` (`CaseScreen.tsx`'s own
 * docblock) — `case.create` is a SEPARATE ability (`CasePolicy`'s own class
 * docblock: "four verbs, four permissions, no bundling"), so a pharmacist
 * or educator (view-only) reaches this exact list with no create control at
 * all, the same split `DeviceScreen.tsx` makes for `device.manage`.
 */

export interface CaseListProps {
  permissions: readonly string[];
  onOpen: (uuid: string) => void;
  /**
   * Whether this list is the view on screen. `CaseScreen` keeps the list
   * mounted (hidden) under an opened case, and `PageActions` portals into the
   * shell's title row whatever `hidden` says — so a hidden list withdraws
   * "New case" and Export, or they would sit beside the case detail.
   */
  active?: boolean;
}

interface CaseListRow extends Row, CaseRow {}

function toRow(node: CaseRow): CaseListRow {
  return { ...node, id: node.uuid };
}

const STATUSES: readonly CaseStatus[] = ['open', 'in_progress', 'waiting', 'escalated', 'closed', 'cancelled'];

interface FilterState {
  caseNumber: string;
  mobile: string;
  status: string;
  replacement: string;
}

const BLANK_FILTERS: FilterState = { caseNumber: '', mobile: '', status: '', replacement: '' };

function toQuery(filters: FilterState, cursor?: string, perPage?: number): CaseQuery {
  const query: CaseQuery = {};
  if (filters.caseNumber.trim() !== '') query.case_number = filters.caseNumber.trim();
  if (filters.mobile.trim() !== '') query.mobile = filters.mobile.trim();
  if (filters.status !== '') query.status = filters.status as CaseStatus;
  if (filters.replacement !== '') {
    query.replacement = filters.replacement as NonNullable<CaseQuery['replacement']>;
  }
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

function hasAppliedFilters(query: CaseQuery): boolean {
  return (
    query.case_number !== undefined ||
    query.mobile !== undefined ||
    query.status !== undefined ||
    query.replacement !== undefined
  );
}

export function CaseList({ permissions, onOpen, active = true }: CaseListProps) {
  const { t, i18n } = useTranslation();
  const canCreate = permissions.includes('case.create');

  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly CaseListRow[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `CaseIndexRequest::DEFAULT_PER_PAGE` server-side; the first render
  // must ask for exactly what the API already defaults to.
  const pages = useCursorPages();
  // Destructured, not `pages.observe` inline below — `pages` itself is a
  // fresh object every render (`useCursorPages` returns an object literal),
  // so depending on the whole object would recreate `load` (and refire the
  // fetch effect) on every render. `observe` is the one method this
  // callback needs and is independently stable across renders.
  const { observe } = pages;
  const [draftFilters, setDraftFilters] = useState<FilterState>(BLANK_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<FilterState>(BLANK_FILTERS);

  const [formOpen, setFormOpen] = useState(false);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: CaseQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await casesApi.list(query);
      if (seq !== requestSeq.current) return;
      setRows(page.data.map(toRow));
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }, [load, appliedFilters, pages.cursor, pages.perPage]);

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedFilters(draftFilters);
  }

  function handleClearFilters(): void {
    setDraftFilters(BLANK_FILTERS);
    pages.first();
    setAppliedFilters(BLANK_FILTERS);
  }

  function retry(): void {
    void load(toQuery(appliedFilters, pages.cursor ?? undefined, pages.perPage));
  }

  function updateDraft(field: keyof FilterState) {
    return (event: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      const { value } = event.target;
      setDraftFilters((current) => ({ ...current, [field]: value }));
    };
  }

  function handleCreated(created: CaseRow): void {
    setRows((current) => [toRow(created), ...current]);
    if (status === 'empty') setStatus('ready');
    setFormOpen(false);
  }

  function renderStatus(row: Row): ReactNode {
    return t(statusLabelKey((row as unknown as CaseRow).status));
  }

  function renderDispensary(row: Row): ReactNode {
    return optionalReferenceName((row as unknown as CaseRow).dispensary, i18n.language);
  }

  /**
   * A date is not a code identifier — it is prose, and it mirrors with the
   * rest of the page. `neverMirrors` is therefore NOT set on this column,
   * unlike the case number and mobile beside it.
   */
  function renderCreatedAt(row: Row): ReactNode {
    // `created_at` is nullable on the resource; DataTable renders its own
    // empty-cell treatment for null, which is why this returns null rather
    // than formatting the string "null".
    const value = (row as unknown as CaseRow).created_at;

    return value === null ? null : formatTimestamp(value, i18n.language);
  }

  /**
   * ONE COLUMN FOR BOTH SERIALS, labelled by which it is.
   *
   * A case almost never carries both — 137,670 migrated cases name a
   * replacement sensor and 11,610 a reader — so two columns would be two
   * mostly-empty ones, and this list already carries five. The label is what
   * disambiguates; the detail screen shows the pair separately.
   */
  function renderReplacement(row: Row): ReactNode {
    const record = row as unknown as CaseRow;
    const parts: string[] = [];

    if (record.replacement_reader_serial !== null && record.replacement_reader_serial !== '') {
      parts.push(`${t('cases.replacementReaderShort')}: ${record.replacement_reader_serial}`);
    }
    if (record.replacement_sensor_serial !== null && record.replacement_sensor_serial !== '') {
      parts.push(`${t('cases.replacementSensorShort')}: ${record.replacement_sensor_serial}`);
    }

    // null, not an em dash: DataTable renders its own empty-cell treatment,
    // the same choice `renderCreatedAt` above makes.
    return parts.length === 0 ? null : <bdi dir="ltr">{parts.join(' · ')}</bdi>;
  }

  const replacementOptions: readonly SelectOption[] = (
    ['any', 'reader', 'sensor', 'none'] as const
  ).map((value) => ({ value, label: t(`cases.replacementFilter.${value}`) }));

  const statusOptions: readonly SelectOption[] = STATUSES.map((value) => ({
    value,
    label: t(statusLabelKey(value)),
  }));

  const columns: readonly Column[] = [
    { key: 'case_number', label: t('table.caseNumber'), neverMirrors: true },
    { key: 'mobile', label: t('table.mobile'), neverMirrors: true },
    { key: 'dispensary', label: t('table.dispensary'), render: renderDispensary },
    { key: 'status', label: t('table.status'), render: renderStatus },
    { key: 'replacement', label: t('table.replacement'), render: renderReplacement },
    { key: 'created_at', label: t('table.createdAt'), render: renderCreatedAt },
  ];

  return (
    <div className={styles['page']}>
      {/* "New case" and Export sit in the shell's title row; the page carries
          no second heading under the shell's `<h1>`. Export is the register
          the list is CURRENTLY showing: `toQuery` is the same function the
          fetch above uses, called without a cursor because an export is the
          whole filtered set (the route 422s on `cursor`). `canExport` is
          unconditional — reaching this screen already implies case read
          access, gated at the router. */}
      {active ? (
        <PageActions>
          {canCreate ? (
            <Button
              onClick={() => {
                setFormOpen(true);
              }}
              icon={<PlusIcon size={16} />}
            >
              {t('actions.newCase')}
            </Button>
          ) : null}
          <ExportButton module="cases" filters={{ ...toQuery(appliedFilters) }} canExport />
        </PageActions>
      ) : null}

      <FilterBar onSubmit={handleFilterSubmit} onClear={handleClearFilters}>
        <Input
          label={t('table.caseNumber')}
          width="compact"
          dir="ltr"
          value={draftFilters.caseNumber}
          onChange={updateDraft('caseNumber')}
        />
        <Input
          label={t('table.mobile')}
          width="phone"
          dir="ltr"
          value={draftFilters.mobile}
          onChange={updateDraft('mobile')}
        />
        <Select
          label={t('table.status')}
          width="compact"
          placeholder={t('cases.anyStatus')}
          options={statusOptions}
          value={draftFilters.status}
          onChange={updateDraft('status')}
        />
        <Select
          label={t('table.replacement')}
          width="compact"
          placeholder={t('cases.anyReplacement')}
          options={replacementOptions}
          value={draftFilters.replacement}
          onChange={updateDraft('replacement')}
        />
      </FilterBar>

      <DataTable
        caption={t('table.caption')}
        columns={columns}
        rows={rows}
        status={status}
        empty={{ body: t('cases.emptyList') }}
        noResults={{ title: t('cases.noResultsList') }}
        onClearFilters={handleClearFilters}
        error={{ title: t('cases.loadFailedList') }}
        onRowActivate={(row) => {
          onOpen(row.id);
        }}
      />

      <Pagination pages={pages} total={total} rowsOnPage={rows.length} />

      {status === 'error' ? (
        <div className={styles['recovery']}>
          <Button variant="secondary" onClick={retry}
            icon={<ArrowClockwiseIcon size={16} />}
          >
            {t('actions.retry')}
          </Button>

          {reference === null ? null : (
            <p className={styles['reference']}>
              {t('auth.supportReference')}{' '}
              <bdi dir="ltr" data-testid="caseListRequestId">
                {reference}
              </bdi>
            </p>
          )}
        </div>
      ) : null}

      <CaseFormDialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        onSaved={handleCreated}
      />
    </div>
  );
}
